import React, { useState } from "react";
import { format, startOfWeek, addDays, isToday, isBefore } from "date-fns";
import { useGetMovesQuery } from "@/store/moves/apis";
import { useGetCalendarEventsQuery } from "@/store/mover/apis";
import { CalendarEvent, Move, User } from "@/types";
import { MOVE_COLORS, MOVE_COLORS_LIGHT } from "@/constants";
import { Link } from "react-router-dom";
import { ClockIcon, MapPinIcon } from "lucide-react";
import { cn, formatService, formatStartWindow } from "@/lib/utils";
import { Button } from "./ui/button";
import { AssignCrew } from "@/pages/Jobs/AssignCrew";
import { faCheck } from "@awesome.me/kit-9096fb77ac/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Loading } from "./Loading";

const WeekCalendar: React.FC<{ currentDate: Date }> = ({ currentDate }) => {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const weekEnd = addDays(weekStart, 6);
  const [selectedMoveForCrew, setSelectedMoveForCrew] = useState<
    Move | undefined
  >(undefined);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  // Fetch moves and events for the week
  const {
    data: moves,
    isLoading,
    error,
    refetch,
  } = useGetMovesQuery({
    type: "jobs",
    after: format(weekStart, "yyyy-MM-dd"),
    before: format(weekEnd, "yyyy-MM-dd"),
    include: ["locations", "crew"],
  });
  const { data: onsiteEvents } = useGetCalendarEventsQuery({
    nature: "onsite",
    after: format(weekStart, "yyyy-MM-dd"),
    before: format(weekEnd, "yyyy-MM-dd"),
  });
  const { data: blockEvents } = useGetCalendarEventsQuery({
    nature: "block",
    after: format(weekStart, "yyyy-MM-dd"),
    before: format(weekEnd, "yyyy-MM-dd"),
  });

  if (isLoading)
    return (
      <div className="mt-4">
        <Loading />
      </div>
    );
  if (error) return <div className="mt-4">Error loading data</div>;
  if (!moves) return null;

  // Helper: get events for a day
  const getEventsForDay = (date: Date) => {
    const dateString = format(date, "yyyy-MM-dd");
    const dayMoves = moves.filter(
      (move: Move) => move.service_date === dateString
    );
    const dayOnsites = (onsiteEvents || []).filter(
      (event: CalendarEvent) => event.date === dateString
    );
    const dayBlocks = (blockEvents || []).filter(
      (event: CalendarEvent) => event.date === dateString
    );
    return { dayMoves, dayOnsites, dayBlocks };
  };

  // Helper: render a move or onsite event
  const renderEventBlock = (event: Move | CalendarEvent) => {
    if ("identifier" in event) {
      return (
        <div
          key={event.id}
          className={cn(
            "w-full mb-4 text-sm bg-white",
            `${
              MOVE_COLORS_LIGHT[
                event.service as keyof typeof MOVE_COLORS_LIGHT
              ] || MOVE_COLORS_LIGHT.default
            }`
          )}
        >
          <Link target="_blank" to={`/move/${event.id}`}>
            <h3
              className={cn(
                "text-sm text-white font-bold px-2 py-0.5",
                `${
                  MOVE_COLORS[event.service as keyof typeof MOVE_COLORS] ||
                  MOVE_COLORS.default
                }`
              )}
            >
              <Link className="text-white" to={`/move/${event.id}`}>
                {event.customer_name}
              </Link>
            </h3>
            <div className="px-2 py-1 text-xs text-black">
              <div>#{event.number}</div>
              <div className="flex flex-col gap-0.5 text-xs">
                <div>{formatService(event)}</div>
                <div className="flex items-center">
                  {/* <ClockIcon className="w-3 h-3 mr-1" /> */}
                  {formatStartWindow(
                    event.start_window_begin_hour,
                    event.start_window_begin_minute,
                    event.start_window_begin_meridiem
                  )}
                  {/* {"-"}
                  {formatStartWindow(
                    event.start_window_end_hour,
                    event.start_window_end_minute,
                    event.start_window_end_meridiem
                  )} */}{" "}
                  ({parseInt(event.hours_estimate?.toString() || "2")}-
                  {parseInt(event.hours_estimate?.toString() || "2") + 1} hrs)
                </div>
                <div className="flex flex-row flex-wrap items-center gap-0.5">
                  <div>{event.origin?.city}</div>
                  {event.stops.map((stop) => (
                    <div
                      className="flex flex-row items-center gap-0.5"
                      key={stop.id}
                    >
                      <span>&rarr;</span>
                      <span>{stop.city}</span>
                    </div>
                  ))}
                  {event.destination?.city && (
                    <div className="flex flex-row items-center gap-0.5">
                      <span>&rarr;</span>
                      <span>{event.destination?.city}</span>
                    </div>
                  )}
                </div>
                {event.assigned_crew?.length > 0 && (
                  <div className="mt-1">
                    <h4 className="text-xs font-semibold">
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="text-green-700 mr-1"
                      />
                      {event.assigned_crew?.length}/{event.num_of_movers} movers
                      assigned
                    </h4>
                    <div className="mt-1 flex flex-col gap-0.5">
                      {event.assigned_crew?.map((crew: User) => (
                        <div
                          key={crew.id}
                          className="flex flex-col items-start gap-0.5"
                        >
                          <span>
                            {crew.first_name} {crew.last_name}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Link>
          <div className="px-2 py-2">
            <Button
              size={null}
              variant="secondary"
              className="w-full rounded-full ring-2 ring-stone-600"
              onClick={() => {
                setSelectedMoveForCrew(event);
                setIsSheetOpen(true);
              }}
            >
              Dispatch Crew
            </Button>
          </div>
        </div>
      );
    } else if (event.nature === "onsite") {
      const color = MOVE_COLORS.onsite;
      return (
        <div
          key={event.id}
          className={`mb-4 w-full ${color} rounded text-white px-2 py-1 text-xs shadow`}
        >
          <Link className="text-white" to={`/move/${event.move_id}`}>
            <div className="font-semibold">{event.title}</div>
            <div className="flex items-center mt-1">
              <ClockIcon className="w-3 h-3 mr-1" />
              {event.start_time} - {event.end_time}
            </div>
            <div className="flex items-center mt-1">
              <MapPinIcon className="w-3 h-3 mr-1" />
              {event.address}
            </div>
          </Link>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="relative">
      {/* Header: Days of week */}
      <div className="grid grid-cols-7 gap-0 border-b">
        {days.map((day) => (
          <div
            key={day.toISOString()}
            className={`text-center py-2 font-semibold text-sm border-l ${
              isToday(day) ? "bg-blue-50 text-blue-700" : "bg-stone-50"
            }`}
          >
            {format(day, "EEE d")}
          </div>
        ))}
      </div>
      {/* Day columns */}
      <div className="py-1 grid grid-cols-7 divide-x divide-stone-200">
        {days.map((day) => {
          const { dayMoves, dayOnsites, dayBlocks } = getEventsForDay(day);
          const isBlocked = dayBlocks.length > 0;
          const isPastOrToday = isBefore(day, new Date()) || isToday(day);
          return (
            <div
              key={day.toISOString()}
              className={`flex flex-col flex-1 relative min-h-[500px]  ${
                isPastOrToday ? "bg-white" : "bg-white"
              }`}
            >
              {/* Blocked day */}
              {isBlocked ? (
                <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center bg-slate-200">
                  <div className="text-slate-700 font-medium bg-slate-100 rounded-md px-3 py-1.5 shadow-sm flex items-center gap-2">
                    <span>🚫</span>
                    <span>Blocked</span>
                  </div>
                </div>
              ) : (
                <>
                  {dayMoves.map((move: Move) => renderEventBlock(move))}
                  {dayOnsites.map((event: CalendarEvent) =>
                    renderEventBlock(event)
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      <AssignCrew
        move_id={selectedMoveForCrew?.id}
        open={isSheetOpen}
        onOpenChange={(open) => {
          setIsSheetOpen(open);
          if (!open) {
            setSelectedMoveForCrew(undefined);
            refetch();
          }
        }}
      />
    </div>
  );
};

export default WeekCalendar;
